/**
 * Every setting a person may look for, with the exact place it lives. The command palette (⌘K)
 * lists the ones the viewer can open; choosing one opens the page and tab and brings the field
 * into view (`focus` = an element id or test id on that tab).
 */
import { resources } from "@ap/i18n";
import type { PaletteItem, Translate } from "./palette";
import { canOpen } from "./tabRoles";

export interface SettingEntry {
  /** Key in `palette:setting` (label and hint) and `palette:settingWords` (synonyms). */
  id: string;
  path: string;
  /** Element id or data-testid to bring into view. */
  focus?: string;
}

export const SETTINGS: SettingEntry[] = [
  { id: "quietHours", path: "/channels?tab=settings", focus: "cs-quiet-start" },
  { id: "channelTimezone", path: "/channels?tab=settings", focus: "cs-timezone" },
  { id: "optOut", path: "/channels?tab=settings", focus: "cs-opt-out" },
  { id: "messageLimits", path: "/channels?tab=settings", focus: "cs-per-hour" },
  { id: "defaultLanguage", path: "/channels?tab=settings", focus: "cs-language" },
  { id: "handover", path: "/channels?tab=settings", focus: "cs-queue" },
  { id: "defaultAgent", path: "/channels?tab=settings", focus: "cs-agent" },
  { id: "websiteChat", path: "/channels?tab=widgets" },
  { id: "connectApps", path: "/channels?tab=connect" },
  { id: "voice", path: "/channels?tab=voice", focus: "voice-tab" },
  { id: "consents", path: "/channels?tab=consents" },
  { id: "speechModels", path: "/models?tab=speech", focus: "voice-choices" },
  { id: "apiKeys", path: "/models?tab=providers" },
  { id: "modelChoices", path: "/models?tab=aliases" },
  { id: "modelBackups", path: "/models?tab=fallbacks" },
  { id: "routingRules", path: "/models?tab=routes" },
  { id: "spendingLimits", path: "/admin?tab=spending" },
  { id: "cache", path: "/models?tab=cache" },
  { id: "allowedWebsites", path: "/admin?tab=internet", focus: "internet-websites" },
  { id: "internetAddresses", path: "/admin?tab=internet", focus: "internet-models" },
  { id: "approvalAmounts", path: "/admin?tab=approvals", focus: "approvals-tab" },
  { id: "usersRoles", path: "/admin?tab=users" },
  { id: "organisation", path: "/admin?tab=tenant", focus: "tenant-settings-form" },
  { id: "dataResidency", path: "/admin?tab=tenant", focus: "ts-region" },
  { id: "onlineModels", path: "/admin?tab=tenant", focus: "ts-online" },
  { id: "platformSettings", path: "/admin?tab=platform", focus: "platform-settings-form" },
  { id: "businessCalendar", path: "/cases/settings?tab=calendar" },
  { id: "privacyRules", path: "/privacy?tab=pii" },
  { id: "rules", path: "/governance?tab=rules" },
  { id: "emergencyStop", path: "/governance?tab=killswitch" },
  { id: "knowledge", path: "/knowledge" },
  { id: "whenItRuns", path: "/builder?tab=triggers" },
  { id: "agentOverview", path: "/agents" },
];

const withFocus = (e: SettingEntry) => (e.focus ? `${e.path}${e.path.includes("?") ? "&" : "?"}focus=${encodeURIComponent(e.focus)}` : e.path);

type Words = Record<string, string>;
interface PaletteBundle {
  settingWords?: Words;
  setting?: Record<string, { label: string }>;
}
const bundle = (lang: "en" | "ar"): PaletteBundle => resources[lang].palette as unknown as PaletteBundle;
/** Synonyms and the label in both languages, so either language finds a setting. */
const searchWords = (id: string): string =>
  (["en", "ar"] as const).map((l) => `${bundle(l).settingWords?.[id] ?? ""} ${bundle(l).setting?.[id]?.label ?? ""}`).join(" ");

/** The settings this viewer can open, as palette rows; synonyms match in both languages. */
export function settingItems(roles: readonly string[], t: Translate): PaletteItem[] {
  return SETTINGS.filter((e) => canOpen(roles, e.path)).map((e) => ({
    id: `setting:${e.id}`,
    kind: "setting" as const,
    label: t(`setting.${e.id}.label`, { ns: "palette" }),
    hint: t(`setting.${e.id}.where`, { ns: "palette" }),
    keywords: searchWords(e.id),
    path: withFocus(e),
  }));
}
